import {
  Divider,
  PrimaryButton,
  Space,
  Text,
  TertiaryButton,
  WarningAlert,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

export interface ILegalEntityReviewProps {
  baseCurrencyName: string
  externalId: string
  country: string
  /** Omitted when the entity has no invoice inbox (AP disabled). */
  previewInboxEmailAddress?: string
  isConfirming: boolean
  onEdit: () => void
  onConfirm: () => void
}

export const LegalEntityReview = ({
  baseCurrencyName,
  externalId,
  country,
  previewInboxEmailAddress,
  isConfirming,
  onEdit,
  onConfirm,
}: ILegalEntityReviewProps) => {
  const intl = useProcurifyIntl()

  const emptyValue = intl.formatMessage(messages.emptyValue)

  const rows = [
    {
      label: intl.formatMessage(messages.baseCurrencyLabel),
      value: baseCurrencyName || emptyValue,
    },
    {
      label: intl.formatMessage(messages.externalIdLabel),
      value: externalId || emptyValue,
    },
    {
      label: intl.formatMessage(messages.countryLabel),
      value: country || emptyValue,
    },
    ...(previewInboxEmailAddress
      ? [
          {
            label: intl.formatMessage(messages.inboxEmailAddressLabel),
            value: previewInboxEmailAddress,
          },
        ]
      : []),
  ]

  return (
    <Space size='large'>
      <Space>
        {rows.map((row) => (
          <Space key={row.label} direction='horizontal' align='start'>
            <Space.Item width={160}>
              <Text>{row.label}</Text>
            </Space.Item>
            <Text bold>{row.value}</Text>
          </Space>
        ))}
        {previewInboxEmailAddress && (
          <WarningAlert>
            {intl.formatMessage(messages.inboxEmailAddressPermanentWarning)}
          </WarningAlert>
        )}
      </Space>

      <Divider space={0} />

      <Space direction='horizontal' justify='end' align='center'>
        <TertiaryButton type='button' onClick={onEdit} disabled={isConfirming}>
          {intl.formatMessage(messages.editButton)}
        </TertiaryButton>
        <PrimaryButton
          type='button'
          onClick={onConfirm}
          isLoading={isConfirming}
        >
          {intl.formatMessage(messages.confirmButton)}
        </PrimaryButton>
      </Space>
    </Space>
  )
}

LegalEntityReview.displayName = 'LegalEntityReview'
